import { ApiError, apiErrorReason } from '@/lib/api';
import { isTypeRestricted, restrictedActionText } from '@/lib/document-capabilities';
import { errorAdvice } from '@/lib/error-advice';

// Mensajes de la edición en línea según el motivo que manda el backend (UX-ERR-01: qué
// pasó y qué hacer). Una sola fuente para la vista y la edición del documento.

const NOT_CONFIGURED =
  'La edición en línea todavía no está disponible. Si la necesitas, avisa al Administrador de Calidad.';

const MICROSOFT_DOWN =
  'Microsoft 365 no responde en este momento, así que la vista del documento no se puede cargar. Vuelve a intentar en unos minutos; si persiste, avisa al Administrador de Calidad.';

const BAD_WORD =
  'El archivo está dañado o no es un documento de Word válido. Vuelve a guardarlo desde Word y cárgalo de nuevo en el gestor documental.';

/** Aviso a Calidad, que puede tomar la versión del gestor documental como borrador. */
export const LMS_DIVERGED_QUALITY_TEXT =
  'El gestor documental tiene una versión de este documento cargada fuera del QMS. Revísala y, si corresponde, tómala como borrador.';

const NO_M365 =
  'No tienes una cuenta de Microsoft 365 en Grupo DIM con el correo registrado en el QMS. Pide al Administrador de Calidad que revise tu correo.';

const VIEW_REASONS: Record<string, string> = {
  no_binary:
    'Este documento todavía no tiene archivo en el gestor documental. La vista y la edición en línea se habilitan al cargarlo.',
  no_official_version:
    'El documento todavía no tiene una versión vigente. El borrador solo lo ven quienes participan de su elaboración, revisión o aprobación.',
  no_viewable_file:
    'La versión vigente no tiene un archivo que se pueda mostrar. Pide al Administrador de Calidad que revise el archivo cargado en el gestor documental.',
  m365_user_not_found: NO_M365,
  online_editing_not_configured: NOT_CONFIGURED,
  microsoft_365_unavailable: MICROSOFT_DOWN,
  document_pdf_not_configured:
    'La vista de documentos todavía no está disponible. Avisa al Administrador de Calidad.',
};

const EDIT_REASONS: Record<string, string> = {
  m365_user_not_found: NO_M365,
  lms_version_diverged:
    'El gestor documental tiene una versión de este documento cargada fuera del QMS. El Administrador de Calidad debe tomarla como borrador antes de editar en línea.',
  external_binary_not_supported:
    'El documento apunta a un archivo externo. Cárgalo en el gestor documental para editarlo en línea.',
  unsupported_file_type:
    'Solo se editan en línea documentos de Word (.docx), Excel (.xlsx) y PowerPoint (.pptx).',
  encrypted_file:
    'El archivo está protegido con contraseña. Quita la protección y vuelve a cargarlo para editarlo en línea.',
  no_binary: VIEW_REASONS['no_binary'] ?? '',
  not_editable_status: 'El documento ya no está en una etapa de edición. Actualiza la página.',
  online_editing_not_configured: NOT_CONFIGURED,
  microsoft_365_unavailable:
    'Microsoft 365 no responde en este momento. Vuelve a intentar en unos minutos; si persiste, avisa al Administrador de Calidad.',
  previous_approval_pending:
    'La versión aprobada anterior todavía no se publica en el gestor documental. Vuelve a intentar en unos minutos; si persiste, el Administrador de Calidad debe reintentar la publicación.',
  cycle_preparing:
    'El documento se está preparando para la edición con control de cambios. Vuelve a intentar en unos segundos.',
  lms_version_not_newer:
    'El gestor documental ya no tiene una versión posterior a la del QMS. Actualiza la página y vuelve a abrir el editor.',
  file_type_changed:
    'La versión del gestor documental es de otro tipo de archivo que el documento en edición. Pide que se cargue en el mismo formato.',
  no_working_copy:
    'Este documento todavía no tiene un borrador en Microsoft 365. Ábrelo primero con el botón Editar en Office.',
  no_active_flow:
    'El documento no tiene un ciclo de elaboración o modificación abierto. Actualiza la página.',
  file_too_large:
    'El archivo supera el tamaño máximo para editar en línea. Pide al Administrador de Calidad que lo revise.',
  not_docx: BAD_WORD,
  invalid_xml: BAD_WORD,
};

/** Por qué no se puede mostrar la vista del documento. */
export function onlineViewIssueText(err: unknown): string {
  const reason = apiErrorReason(err);
  const known = reason === undefined ? undefined : VIEW_REASONS[reason];
  return known ?? `No se pudo cargar la vista del documento. ${errorAdvice(err)}`;
}

/** La vista no se puede mostrar por el estado del documento (no por la red). */
export function isViewUnavailable(err: unknown): boolean {
  return err instanceof ApiError && err.status === 422;
}

/** Por qué no se pudo editar o capturar; `fallback` cuando el motivo no se conoce. */
export function onlineEditErrorText(err: unknown, fallback: string): string {
  if (isTypeRestricted(err)) {
    return restrictedActionText('editar en línea');
  }
  const reason = apiErrorReason(err);
  return (reason === undefined ? undefined : EDIT_REASONS[reason]) ?? fallback;
}
